import { useMutation, useQuery } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, useScopedMerchantClient } from "../scoped-merchant-client";

/**
 * Minimal flag type for bulk operations - only needs id and videoId
 */
export type BulkFlagData = {
  id: string;
  videoId: string;
};

/**
 * Fetch flags for multiple videos in parallel.
 * Returns all pending (unreviewed) flags across the selected videos.
 */
export const useBulkVideoFlags = (videoIds: string[], enabled = true) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["bulk-video-flags", scope.merchantId, scope.workspaceId, videoIds.join(",")],
    queryFn: async () => {
      if (!scope.merchantId || videoIds.length === 0) return [];

      const results = await Promise.all(
        videoIds.map(async (videoId) => {
          const res = scope.workspaceId
            ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.video[":videoId"].$get({
                param: { ...scope.param, videoId },
                query: {},
              })
            : await client.merchant[":merchantId"].assurance.video[":videoId"].$get({
                param: { ...scope.param, videoId },
                query: {},
              });
          if (!res.ok) throw await handleError(res);
          const video = await res.json();
          return video.flags;
        }),
      );

      // Flatten all flags from all videos
      const allFlags = results.flat();

      // Filter to only pending/unreviewed flags (flags with no edits at all)
      const pendingFlags = allFlags.filter((flag) => flag.edits.length === 0);

      return pendingFlags;
    },
    enabled: !!scope.merchantId && videoIds.length > 0 && enabled,
  });
};

const invalidateQueriesForFlags = (merchantId: string, workspaceId: string | null, flags: BulkFlagData[]) => {
  // Get unique video IDs from flags
  const videoIds = [...new Set(flags.map((f) => f.videoId))];

  for (const videoId of videoIds) {
    queryClient.invalidateQueries({ queryKey: ["video", merchantId, workspaceId, videoId] });
    queryClient.invalidateQueries({ queryKey: ["assurance-video", merchantId, workspaceId, videoId] });
  }

  queryClient.invalidateQueries({ queryKey: ["video", merchantId] });
  queryClient.invalidateQueries({ queryKey: ["assurance-video", merchantId] });
  queryClient.invalidateQueries({ queryKey: ["assurance-overview", merchantId] });
  queryClient.invalidateQueries({ queryKey: ["assurance-overview-v2", merchantId] });
  queryClient.invalidateQueries({ queryKey: ["portfolio-assurance-overview"] });
  queryClient.invalidateQueries({ queryKey: ["bulk-video-flags", merchantId] });
};

/**
 * Bulk approve all provided flags.
 * Sets actionTaken to "flag_is_correct" for each flag.
 * Uses Promise.allSettled to handle partial successes gracefully.
 */
export const useBulkApproveFlags = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (flags: BulkFlagData[]) => {
      if (!scope.merchantId || flags.length === 0) return { successful: [], failed: [] };

      const results = await Promise.allSettled(
        flags.map(async (flag) => {
          try {
            const res = scope.workspaceId
              ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.flag[":id"].$put({
                  json: {
                    actionTaken: "flag_is_correct",
                    feedbackReason: "Bulk approved",
                  },
                  param: { ...scope.param, id: flag.id },
                })
              : await client.merchant[":merchantId"].assurance.flag[":id"].$put({
                  json: {
                    actionTaken: "flag_is_correct",
                    feedbackReason: "Bulk approved",
                  },
                  param: { ...scope.param, id: flag.id },
                });
            if (!res.ok) throw await handleError(res);
            await res.json();
            return flag;
          } catch (err) {
            throw { flag, error: err };
          }
        }),
      );

      const successful: BulkFlagData[] = [];
      const failed: Array<{ flag: BulkFlagData; error: unknown }> = [];

      for (const result of results) {
        if (result.status === "fulfilled") {
          successful.push(result.value);
        } else {
          const errorData = result.reason as { flag: BulkFlagData; error: unknown };
          failed.push(errorData);
        }
      }

      return { successful, failed };
    },
    onSuccess: (result) => {
      if (scope.merchantId && result && result.successful.length > 0) {
        invalidateQueriesForFlags(scope.merchantId, scope.workspaceId, result.successful);
      }
    },
  });
};

/**
 * Bulk reject all provided flags.
 * Sets actionTaken to "flag_dismissed" with a feedback reason.
 * Uses Promise.allSettled to handle partial successes gracefully.
 */
export const useBulkRejectFlags = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({ flags, feedbackReason }: { flags: BulkFlagData[]; feedbackReason: string }) => {
      if (!scope.merchantId || flags.length === 0) return { successful: [], failed: [] };

      const results = await Promise.allSettled(
        flags.map(async (flag) => {
          try {
            const res = scope.workspaceId
              ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.flag[":id"].$put({
                  json: {
                    actionTaken: "flag_dismissed",
                    feedbackReason,
                  },
                  param: { ...scope.param, id: flag.id },
                })
              : await client.merchant[":merchantId"].assurance.flag[":id"].$put({
                  json: {
                    actionTaken: "flag_dismissed",
                    feedbackReason,
                  },
                  param: { ...scope.param, id: flag.id },
                });
            if (!res.ok) throw await handleError(res);
            await res.json();
            return flag;
          } catch (err) {
            throw { flag, error: err };
          }
        }),
      );

      const successful: BulkFlagData[] = [];
      const failed: Array<{ flag: BulkFlagData; error: unknown }> = [];

      for (const result of results) {
        if (result.status === "fulfilled") {
          successful.push(result.value);
        } else {
          const errorData = result.reason as { flag: BulkFlagData; error: unknown };
          failed.push(errorData);
        }
      }

      return { successful, failed };
    },
    onSuccess: (result) => {
      if (scope.merchantId && result && result.successful.length > 0) {
        invalidateQueriesForFlags(scope.merchantId, scope.workspaceId, result.successful);
      }
    },
  });
};

type BulkEditFlagsPayload = {
  flags: BulkFlagData[];
  riskTier: number;
  riskTypeShort: string;
  explanation: string;
  feedbackReason: string;
};

/**
 * Bulk edit all provided flags with new risk tier, type, and feedback.
 * Sets actionTaken to "edit_flag" for each flag.
 * Uses Promise.allSettled to handle partial successes gracefully.
 */
export const useBulkEditFlags = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({ flags, riskTier, riskTypeShort, explanation, feedbackReason }: BulkEditFlagsPayload) => {
      if (!scope.merchantId || flags.length === 0) return { successful: [], failed: [] };

      const results = await Promise.allSettled(
        flags.map(async (flag) => {
          try {
            const res = scope.workspaceId
              ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.flag[":id"].$put({
                  json: {
                    actionTaken: "edit_flag",
                    riskTier,
                    riskTypeShort,
                    explanation,
                    feedbackReason,
                  },
                  param: { ...scope.param, id: flag.id },
                })
              : await client.merchant[":merchantId"].assurance.flag[":id"].$put({
                  json: {
                    actionTaken: "edit_flag",
                    riskTier,
                    riskTypeShort,
                    explanation,
                    feedbackReason,
                  },
                  param: { ...scope.param, id: flag.id },
                });
            if (!res.ok) throw await handleError(res);
            await res.json();
            return flag;
          } catch (err) {
            throw { flag, error: err };
          }
        }),
      );

      const successful: BulkFlagData[] = [];
      const failed: Array<{ flag: BulkFlagData; error: unknown }> = [];

      for (const result of results) {
        if (result.status === "fulfilled") {
          successful.push(result.value);
        } else {
          const errorData = result.reason as { flag: BulkFlagData; error: unknown };
          failed.push(errorData);
        }
      }

      return { successful, failed };
    },
    onSuccess: (result) => {
      if (scope.merchantId && result && result.successful.length > 0) {
        invalidateQueriesForFlags(scope.merchantId, scope.workspaceId, result.successful);
      }
    },
  });
};
